import type { AuthProvider } from 'ra-core'
import { extractErrorMessage, HttpError, httpClient } from '../lib/httpClient'
import { type AuthToken, clearToken, decodeJwt, getToken, hasRequiredScope, isTokenValid, setToken } from '../lib/authToken'

interface TokenResponse {
  access_token: string
  token_type: string
}

interface OAuthLoginResponse {
  redirect: string
}

interface OAuthCallbackResponse {
  token: string
  redirect?: string
}

// ra-core's useLogout only avoids re-triggering LogoutOnMount in a loop if checkAuth/
// getPermissions don't resolve instantly (marmelab/react-admin#10769) — ours did.
function yieldToNextMacrotask(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0))
}

export interface AuthProviderBundle {
  authProvider: AuthProvider
  /** Kicks off the Google OAuth flow: fetches the provider's auth URL and full-page navigates there. */
  startGoogleLogin: (redirectTo?: string) => Promise<void>
  signUp: (params: {
    email: string
    password: string
    first_name: string
    last_name: string
    company_name: string
    insurance: string
  }) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  resetPassword: (password: string, token: string) => Promise<void>
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>
}

/**
 * The login/JWT/token-refresh mechanics shared by every app on this backend
 * (`docs/library-extraction.md` §2, §5) — `requiredScopes` is the one thing
 * that varies per app (the admin portal gates on `admin`/`superadmin`, a
 * future specialists portal would gate on `doctor`), so it's the caller's
 * own parameter rather than a hardcoded constant.
 */
export function createAuthProvider({
  requiredScopes,
  accessDeniedMessage = 'Your account does not have access to this application.',
}: {
  requiredScopes: string[]
  /** Defaults to a generic message; pass an app-specific one (e.g. "...admin access.") if it should name the required role. */
  accessDeniedMessage?: string
}): AuthProviderBundle {
  function storeIfScoped(token: AuthToken): void {
    if (!hasRequiredScope(token, requiredScopes)) {
      throw new Error(accessDeniedMessage)
    }
    setToken(token)
  }

  const authProvider: AuthProvider = {
    async login({ email, password }: { email: string; password: string }) {
      let token: TokenResponse
      try {
        token = await httpClient<TokenResponse>('/auth/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ username: email, password }).toString(),
        })
      } catch (error) {
        throw new Error(extractErrorMessage(error, 'Sign in failed. Please try again.'))
      }
      storeIfScoped(token)
    },

    async logout() {
      clearToken()
    },

    async checkAuth() {
      await yieldToNextMacrotask()
      const token = getToken()
      if (!isTokenValid(token)) {
        throw new Error('Not authenticated')
      }
      if (!hasRequiredScope(token, requiredScopes)) {
        throw new Error(accessDeniedMessage)
      }
    },

    async checkError(error: unknown) {
      const status = error instanceof HttpError ? error.status : undefined
      if (status === 401 || status === 403) {
        clearToken()
        throw error
      }
    },

    async getIdentity() {
      const token = getToken()
      const claims = token ? decodeJwt(token.access_token) : null
      if (!claims) {
        throw new Error('Not authenticated')
      }
      return { id: claims.user_id }
    },

    async getPermissions() {
      await yieldToNextMacrotask()
      const token = getToken()
      const claims = token ? decodeJwt(token.access_token) : null
      return claims?.scopes ?? []
    },

    async handleCallback() {
      const params = Object.fromEntries(new URLSearchParams(window.location.search))
      let response: OAuthCallbackResponse
      try {
        response = await httpClient<OAuthCallbackResponse>('/auth/oauth/google/callback', {
          method: 'POST',
          body: JSON.stringify({ params }),
        })
      } catch (error) {
        throw new Error(extractErrorMessage(error, 'Google sign in failed. Please try again.'))
      }
      // OAuth callback returns `{ token }`; every other endpoint returns `{ access_token, token_type }`.
      storeIfScoped({ access_token: response.token, token_type: 'Bearer' })
      return { redirectTo: response.redirect ?? '/' }
    },
  }

  async function startGoogleLogin(redirectTo?: string): Promise<void> {
    let response: OAuthLoginResponse
    try {
      response = await httpClient<OAuthLoginResponse>('/auth/oauth/google/login', {
        method: 'POST',
        body: JSON.stringify({ redirect: redirectTo ?? null }),
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Could not start Google sign in.'))
    }
    window.location.assign(response.redirect)
  }

  async function signUp(params: {
    email: string
    password: string
    first_name: string
    last_name: string
    company_name: string
    insurance: string
  }): Promise<void> {
    let token: TokenResponse
    try {
      token = await httpClient<TokenResponse>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ ...params, link_based_email_verification: true }),
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Sign up failed. Please try again.'))
    }
    storeIfScoped(token)
  }

  async function requestPasswordReset(email: string): Promise<void> {
    try {
      await httpClient('/auth/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Could not request a password reset. Please try again.'))
    }
  }

  async function resetPassword(password: string, token: string): Promise<void> {
    try {
      await httpClient('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ password, token }),
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Could not reset your password. Please try again.'))
    }
  }

  async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      await httpClient('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Could not change your password. Please try again.'))
    }
  }

  return { authProvider, startGoogleLogin, signUp, requestPasswordReset, resetPassword, changePassword }
}
